import { createSlice } from "@reduxjs/toolkit";
import { CollaborationsSlice, SortOptions } from "types/campaign/collaborations";
import { fetchAllCollaborationsThunk, fetchCollaboration, fetchCollaborationBasicThunk, fetchCollaborations } from "./thunk";
import { ERROR } from "types";
import { fetchCollaborationMilestoneData, fetchCollaborationPostThunk } from "./milestones/thunk";
import { defaultFiltersValue } from "Components/campaigns/collaborations/milestone/Filters";
import { defaultCollaborationFilters } from "pages/Campaigns/Collaborations";

const initialState: CollaborationsSlice = {
    error: null,
    loading: true,
    collaborationError: null,
    collaborationLoading: false,
    campaigns: {},
    applied_filter_id: null,
    filters: defaultCollaborationFilters,
    tableFilters: {
        page: 1,
        has_approved_posts: "y",
        sort: SortOptions.MOST_VIEWS
    },
    collaborations: {
        defaultFilters: defaultFiltersValue,
        applied_filter_id: null,
        total: 0,
        filtered_lists: {},
        loading: true,
        error: null,
        gigSteps: [],
    },
    pageLoading: true,
    collaborationPosts: {},
    collaborationPostLoading: false,
}

const collaborationsSlice = createSlice({
    name: 'collaborations',
    initialState,
    reducers: {
        updatePostStatus: (state, action) => {
            const { campaignId, collaborationId, postId, status } = action.payload;

            const filteredLists = state.campaigns[campaignId]?.filtered_lists ? { ...state.campaigns[campaignId].filtered_lists } : {};

            for (let filterId of Object.keys(filteredLists)) {

                const list = filteredLists[filterId].list.map(collaboration => {
                    if (collaboration.uuid === collaborationId) {
                        const posts = collaboration.posts.map(post => {
                            if (post.social_post.uuid === postId) {
                                post = { ...post, status };
                            }
                            return post;
                        })
                        collaboration = { ...collaboration, posts };
                    }

                    return collaboration;
                });

                filteredLists[filterId] = {
                    ...filteredLists[filterId],
                    list,
                }

            }

            const collaborations = state.campaigns[campaignId]?.collaborations ? { ...state.campaigns[campaignId].collaborations } : {};

            let collaboration = collaborations[collaborationId]?.collaboration;
            if (collaboration) {
                const posts = collaboration.posts.map(post => {
                    if (post.social_post.uuid === postId) {
                        post = { ...post, status };
                    }
                    return post;
                })
                collaboration = { ...collaboration, posts };
                collaborations[collaborationId] = {
                    ...collaborations[collaborationId],
                    collaboration,
                }
            }


            state.campaigns[campaignId] = {
                ...state.campaigns[campaignId],
                filtered_lists: filteredLists,
                collaborations
            }
        },
        updateCollaborationFilters: (state, { payload }) => {
            state.filters = payload;
        },
        setCollaborationsSearchQeury: (state, action) => {
            state.filters.q = action.payload;
            state.filters.page = 1;
        },
        updateCollaborationTableFilters: (state, { payload }) => {
            state.tableFilters = payload;
        },
        setPageLoading: (state, { payload }) => {
            state.pageLoading = payload;
        },
        updateMilestoneCollaborationFilters: (state, {payload}) => {
            state.collaborations.defaultFilters = payload;
        },
        updateCollabEarning: (state, {payload}) => {
            const { campaignId, collaborationId, amount } = payload;

            const filteredLists = state.campaigns[campaignId]?.filtered_lists ? { ...state.campaigns[campaignId].filtered_lists } : {};

            for (let filterId of Object.keys(filteredLists)) {

                const list = filteredLists[filterId].list.map(collaboration => {
                    if (collaboration.uuid === collaborationId) {
                        collaboration = { ...collaboration, total_earnings: amount };
                    }

                    return collaboration;
                });

                filteredLists[filterId] = {
                    ...filteredLists[filterId],
                    list,
                }
            }
            state.campaigns[campaignId] = {
                ...state.campaigns[campaignId],
                filtered_lists: filteredLists,
            }
        },
        invalidateCampaignCollaborationLists: (state, { payload }: { payload: { campaignId: string } }) => {
            const { campaignId } = payload;
            const campaignData = state.campaigns[campaignId];
            if (!campaignData?.filtered_lists) return;

            const filteredLists = { ...campaignData.filtered_lists };
            for (const filterId of Object.keys(filteredLists)) {
                filteredLists[filterId] = {
                    ...filteredLists[filterId],
                    expired: true,
                    last_fetched_at: 0,
                };
            }

            state.campaigns[campaignId] = {
                ...campaignData,
                filtered_lists: filteredLists,
            };
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchCollaborations.pending, (state) => {
                state.error = null;
                state.loading = true;
            })
            .addCase(fetchCollaborations.fulfilled, (state, action) => {
                state.loading = false;
                state.error = null;
                const filterId = action.payload.filterId;
                const campaignId = action.payload.campaignId;
                const existingData = state.campaigns?.[campaignId]?.filtered_lists?.[filterId]?.list ?? [];
                const finalList = action.payload.filters.page === 1 ? action.payload.collaborations.items : [...existingData, ...action.payload.collaborations.items];
                state.campaigns[action.payload.campaignId] = {
                    ...state.campaigns[action.payload.campaignId],
                    filtered_lists: {
                        ...(state.campaigns[campaignId]?.filtered_lists ?? {}),
                        [filterId]: {
                            list: finalList,
                            page: action.payload.filters.page,
                            expired: false,
                            last_fetched_at: Date.now(),
                            has_more: action.payload.collaborations.count > finalList.length,
                            filters: action.payload.filters,
                            count: action.payload.collaborations.count
                        }
                    },
                }
                state.applied_filter_id = filterId;

            })
            .addCase(fetchCollaborations.rejected, (state, { payload }) => {
                state.loading = false;
                if (payload !== ERROR.CACHED)
                    state.error = payload || "Failed to fetch collaborations!"
            })

            .addCase(fetchCollaboration.pending, (state) => {
                state.collaborationError = null;
                state.collaborationLoading = true;
            })
            .addCase(fetchCollaboration.fulfilled, (state, { payload }) => {
                state.collaborationError = null;
                state.collaborationLoading = false;

                const { collaboration, collaborationId, campaignId, milestones, chat } = payload;
                const existingData = state.campaigns[campaignId] ?? {};
                const existingCollaborations = existingData.collaborations ?? {};
                state.campaigns[campaignId] = {
                    ...existingData,
                    collaborations: {
                        ...existingCollaborations,
                        [collaborationId]: {
                            collaboration,
                            milestones,
                            chat,
                            last_fetched_at: Date.now()
                        }
                    }
                }

            })
            .addCase(fetchCollaboration.rejected, (state, { payload }) => {
                state.collaborationLoading = false;
                state.collaborationError = payload || "Failed to fetch collaboration!"
            })
            .addCase(fetchCollaborationBasicThunk.pending, (state) => {
                state.collaborationError = null;
                state.collaborationLoading = true;
            })
            .addCase(fetchCollaborationBasicThunk.fulfilled, (state, { payload }) => {
                state.collaborationError = null;
                state.collaborationLoading = false;

                const { collaboration, collaborationId, campaignId } = payload;
                const existingData = state.campaigns[campaignId] ?? {};
                const existingCollaborations = existingData.basic_collaborations ?? {};
                state.campaigns[campaignId] = {
                    ...existingData,
                    basic_collaborations: {
                        ...existingCollaborations,
                        [collaborationId]: {
                            collaboration,
                            last_fetched_at: Date.now()
                        }
                    }
                }

            })
            .addCase(fetchCollaborationBasicThunk.rejected, (state, { payload }) => {
                state.collaborationLoading = false;
                state.collaborationError = payload || "Failed to fetch collaboration!"
            })

            .addCase(fetchAllCollaborationsThunk.pending, (state) => {
                state.collaborations.loading = true;
                state.collaborations.error = null;
            })
            .addCase(fetchAllCollaborationsThunk.fulfilled, (state, { payload }) => {
                const {filterId, filters, count, items, gig_steps} = payload;
                const existingList = state.collaborations.filtered_lists?.[filterId]?.list ?? [];
                const list = filters.page === 1 ? items : [...existingList, ...items]
                state.collaborations.filtered_lists = {
                    ...state.collaborations.filtered_lists,
                    [filterId]: {
                        list,
                        page: filters.page,
                        expired: false,
                        last_fetched_at: Date.now(),
                        has_more: count > list.length,
                        count
                    }
                }
                state.collaborations.gigSteps = gig_steps;
                state.applied_filter_id = filterId
                state.collaborations.loading = false;
                state.collaborations.error = null;

            })
            .addCase(fetchAllCollaborationsThunk.rejected, (state) => {
                state.collaborations.loading = false;
            })
            .addCase(fetchCollaborationMilestoneData.fulfilled, (state, { payload }) => {
                const { campaignId, milestoneId, collaborationId, data, cache } = payload;

                if (!cache) {
                    const filtered_lists = {...state.collaborations.filtered_lists};
                    for(let filterId of Object.keys(filtered_lists)){
                        filtered_lists[filterId] = {...filtered_lists[filterId]};
                        const updated_list = [...filtered_lists[filterId].list];
                        for (let i in updated_list) {
                            const collab = updated_list[i];
                            if (collab?.campaign?.uuid === campaignId && collab?.uuid === collaborationId && String(collab.active_milestone?.id) === String(milestoneId)) {
                                updated_list[i] = {
                                    ...collab,
                                    active_steps: data.active_steps
                                }
                            }
                        }
                        filtered_lists[filterId].list = updated_list
                    }
                    state.collaborations.filtered_lists = filtered_lists;
                    
                }

            })

            .addCase(fetchCollaborationPostThunk.pending, (state) => {
                state.collaborationPostLoading = true;
            })
            .addCase(fetchCollaborationPostThunk.fulfilled, (state, { payload }) => {
                state.collaborationPostLoading = false;
                const {collaborationId, social_post, media_url, postId} = payload;
                const existingPosts = state.collaborationPosts[collaborationId] ?? {};
                state.collaborationPosts[collaborationId] = {
                    ...existingPosts,
                    [postId]: {
                        social_post: social_post,
                        media_url,
                        last_fetched_at: Date.now()
                    }
                }
            })
            .addCase(fetchCollaborationPostThunk.rejected, (state, { payload }) => {
                state.collaborationPostLoading = false;
            })
    }
})

export const {
    setPageLoading,
    updateCollabEarning,
    updatePostStatus,
    updateCollaborationFilters,
    updateCollaborationTableFilters,
    updateMilestoneCollaborationFilters,
    setCollaborationsSearchQeury,
    invalidateCampaignCollaborationLists,
} = collaborationsSlice.actions;

export default collaborationsSlice.reducer;